import { useState, useRef, useCallback, useLayoutEffect } from "react";

/**
 * Move the contiguous block list[start..end] (inclusive) so it is inserted
 * before original index `dest` (0..list.length). Pure; returns a new array.
 * No-op (returns a copy) when dest falls inside/adjacent to the block.
 */
export function moveBlock(list, start, end, dest) {
  if (dest >= start && dest <= end + 1) return list.slice();
  const blockLen = end - start + 1;
  const block = list.slice(start, end + 1);
  const rest = [...list.slice(0, start), ...list.slice(end + 1)];
  let insertAt = dest > end ? dest - blockLen : dest;
  insertAt = Math.max(0, Math.min(insertAt, rest.length));
  rest.splice(insertAt, 0, ...block);
  return rest;
}

/**
 * useDragAndDropList - Custom hook for drag-and-drop reordering of a list
 * with optional FLIP animation for smooth visual transitions.
 *
 * When containerRef is provided:
 *  - During drag: items visually shift via CSS transforms to show where the
 *    dragged item will land (no React re-render, no DOM recreation).
 *  - On drop: actual list reorder + FLIP animation.
 *
 * @param {Array} list - The array to be reordered.
 * @param {Function} setList - Setter for the array.
 * @param {Function} [onReorder] - Optional callback after reorder (receives new list).
 * @param {React.RefObject} [containerRef] - Ref to the list container; enables animation.
 *   Items must have a `data-item-id` attribute.
 * @param {Function} [getMoveRange] - Optional `(index, list) => [start, end]` resolver. When
 *   provided, a drag moves the contiguous block [start..end] (inclusive) instead of a single
 *   item; when omitted, behavior is unchanged (single-item move).
 * @returns {Object} Drag state and handlers for use in draggable elements. Includes `draggedRange`
 *   (`[start, end]` while a grouped drag is active, else `undefined`).
 */
export default function useDragAndDropList(list, setList, onReorder, containerRef, getMoveRange) {
  const grouped = typeof getMoveRange === "function";
  const [draggedIndex, setDraggedIndex] = useState(undefined);
  const [dragOverIndex, setDragOverIndex] = useState(undefined);
  const [draggedRange, setDraggedRange] = useState(undefined);
  const positionsRef = useRef({});
  const shouldAnimateRef = useRef(false);
  // Stable refs for animated drag
  const dragOriginRef = useRef(undefined);
  const dropTargetRef = useRef(undefined);
  // Grouped (block) drag refs
  const dragRangeRef = useRef(null); // { start, end }
  const dropDestRef = useRef(null); // insert-before index in the original list

  // ── FLIP helpers ────────────────────────────────────────────

  const capturePositions = useCallback(() => {
    if (!containerRef?.current) return;
    const children = containerRef.current.querySelectorAll("[data-item-id]");
    const positions = {};
    children.forEach((el) => {
      positions[el.dataset.itemId] = el.getBoundingClientRect();
    });
    positionsRef.current = positions;
    shouldAnimateRef.current = true;
  }, [containerRef]);

  // Runs after React re-renders with a new list (FLIP play phase)
  useLayoutEffect(() => {
    if (!shouldAnimateRef.current || !containerRef?.current) return;
    shouldAnimateRef.current = false;

    const oldPositions = positionsRef.current;
    if (!Object.keys(oldPositions).length) return;

    const children = containerRef.current.querySelectorAll("[data-item-id]");

    children.forEach((el) => {
      const id = el.dataset.itemId;
      const oldRect = oldPositions[id];
      if (!oldRect) return;

      const newRect = el.getBoundingClientRect();
      const deltaY = oldRect.top - newRect.top;
      if (Math.abs(deltaY) < 1) return;

      el.style.transform = `translateY(${deltaY}px)`;
      el.style.transition = "none";

      requestAnimationFrame(() => {
        el.style.transition = "transform 200ms cubic-bezier(0.2, 0, 0, 1)";
        el.style.transform = "";
        el.addEventListener(
          "transitionend",
          () => {
            el.style.transition = "";
            el.style.transform = "";
          },
          { once: true }
        );
      });
    });

    positionsRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  // ── Visual shift during drag (CSS-only, no state changes) ──

  const applyDragShifts = useCallback(
    (fromIndex, toIndex) => {
      if (!containerRef?.current) return;
      const children = [...containerRef.current.querySelectorAll("[data-item-id]")];
      if (!children[fromIndex]) return;

      // Use the gap left by the dragged item + margin
      const draggedRect = children[fromIndex].getBoundingClientRect();
      const shiftAmount = draggedRect.height;

      children.forEach((el, i) => {
        if (i === fromIndex) return;

        let shift = 0;
        if (fromIndex < toIndex && i > fromIndex && i <= toIndex) {
          shift = -shiftAmount;
        } else if (fromIndex > toIndex && i >= toIndex && i < fromIndex) {
          shift = shiftAmount;
        }

        el.style.transition = "transform 200ms cubic-bezier(0.2, 0, 0, 1)";
        el.style.transform = shift ? `translateY(${shift}px)` : "";
      });
    },
    [containerRef]
  );

  const clearDragShifts = useCallback(() => {
    if (!containerRef?.current) return;
    containerRef.current.querySelectorAll("[data-item-id]").forEach((el) => {
      el.style.transition = "";
      el.style.transform = "";
    });
  }, [containerRef]);

  // Block-aware shift: the dragged block [start..end] stays put (it gets the
  // .dragging style); rows between the block and the insert point shift by the
  // block's total height to open a gap. dest = insert-before index (original).
  const applyGroupShifts = useCallback(
    (start, end, dest) => {
      if (!containerRef?.current) return;
      const children = [...containerRef.current.querySelectorAll("[data-item-id]")];
      if (!children[start]) return;

      let blockHeight = 0;
      for (let i = start; i <= end; i++) {
        blockHeight += children[i]?.getBoundingClientRect().height || 0;
      }

      children.forEach((el, i) => {
        let shift = 0;
        if (i >= start && i <= end) {
          shift = 0; // part of the dragged block
        } else if (dest > end + 1 && i > end && i <= dest - 1) {
          shift = -blockHeight; // block moving down
        } else if (dest < start && i >= dest && i <= start - 1) {
          shift = blockHeight; // block moving up
        }
        el.style.transition = "transform 200ms cubic-bezier(0.2, 0, 0, 1)";
        el.style.transform = shift ? `translateY(${shift}px)` : "";
      });
    },
    [containerRef]
  );

  // ── Event handlers ─────────────────────────────────────────

  const handleDragStart = (index) => (e) => {
    setDraggedIndex(index);
    if (grouped) {
      const [start, end] = getMoveRange(index, list);
      dragRangeRef.current = { start, end };
      dropDestRef.current = null;
      setDraggedRange([start, end]);
    } else {
      dragOriginRef.current = index;
      dropTargetRef.current = index;
    }
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    setDragOverIndex(index);

    if (!containerRef?.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const clientY = e.clientY;

    if (grouped) {
      const range = dragRangeRef.current;
      if (!range) return;
      const { start, end } = range;
      // Insert before the hovered row (top half) or after it (bottom half)
      let dest = clientY < midY ? index : index + 1;
      // Dropping inside the dragged block itself is a no-op
      if (dest >= start && dest <= end + 1) dest = start;
      if (dropDestRef.current === dest) return;
      dropDestRef.current = dest;
      applyGroupShifts(start, end, dest);
      return;
    }

    const fromIndex = dragOriginRef.current;
    if (fromIndex === undefined) return;

    let effectiveTarget = index;
    if (fromIndex < index && clientY < midY) effectiveTarget = index - 1;
    if (fromIndex > index && clientY > midY) effectiveTarget = index + 1;
    if (effectiveTarget < 0) effectiveTarget = 0;
    if (list && effectiveTarget >= list.length) effectiveTarget = list.length - 1;
    if (effectiveTarget === fromIndex) effectiveTarget = fromIndex;

    if (dropTargetRef.current === effectiveTarget) return;
    dropTargetRef.current = effectiveTarget;
    applyDragShifts(fromIndex, effectiveTarget);
  };

  // Commit a reorder from the drag refs (animated mode, i.e. containerRef set).
  // Shared by per-row drop and the container-level drop below, so a release that
  // lands in the visual gap (a hit-test hole over the container) reorders too.
  const commitReorderFromRefs = () => {
    if (!containerRef?.current) return;
    clearDragShifts();
    if (grouped) {
      const range = dragRangeRef.current;
      const dest = dropDestRef.current;
      if (range && dest != null) {
        const { start, end } = range;
        if (!(dest >= start && dest <= end + 1)) {
          capturePositions();
          const newList = moveBlock(list, start, end, dest);
          setList(newList);
          if (onReorder) onReorder(newList);
        }
      }
    } else {
      const fromIndex = dragOriginRef.current;
      const toIndex = dropTargetRef.current;
      if (fromIndex !== undefined && toIndex !== undefined && fromIndex !== toIndex) {
        capturePositions();
        const newList = [...list];
        const [removed] = newList.splice(fromIndex, 1);
        newList.splice(toIndex, 0, removed);
        setList(newList);
        if (onReorder) onReorder(newList);
      }
    }
  };

  const resetDragState = () => {
    setDraggedIndex(undefined);
    setDragOverIndex(undefined);
    setDraggedRange(undefined);
    dragOriginRef.current = undefined;
    dropTargetRef.current = undefined;
    dragRangeRef.current = null;
    dropDestRef.current = null;
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();

    if (containerRef?.current) {
      commitReorderFromRefs();
    } else {
      // Legacy mode (no containerRef) — index-based
      if (draggedIndex !== undefined && draggedIndex !== index) {
        const newList = [...list];
        const [removed] = newList.splice(draggedIndex, 1);
        newList.splice(index, 0, removed);
        setList(newList);
        if (onReorder) onReorder(newList);
      }
    }

    resetDragState();
  };

  // Attach these to the list CONTAINER (the element holding the rows). The shift
  // transforms that open the drop gap also move rows out of the way for hit-testing,
  // so the gap is a hole over the container. Without a drop target on the container,
  // releasing in the gap drops on unhandled space and the browser rejects the drag —
  // the item snaps back. The container catches those releases (and row drops bubble
  // here too), completing the reorder from the dest the row dragovers tracked.
  const handleContainerDragOver = (e) => {
    if (!containerRef?.current) return;
    e.preventDefault();
  };

  const handleContainerDrop = (e) => {
    if (!containerRef?.current) return;
    e.preventDefault();
    commitReorderFromRefs();
    resetDragState();
  };

  const handleDragEnd = () => {
    clearDragShifts();
    resetDragState();
  };

  return {
    draggedIndex,
    dragOverIndex,
    draggedRange,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    handleContainerDragOver,
    handleContainerDrop,
    capturePositions,
  };
}
